import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { clearSAToken } from "@/services/superadminApi";
import ConexoLogo from "@/components/ui/ConexoLogo";
import {
    LogOut, LayoutDashboard, Store, ShoppingCart, Map, Users,
    CreditCard, Receipt, ShieldAlert, Terminal, Settings, ChevronRight,
    Zap, ToggleRight, BookOpen, Menu, X
} from 'lucide-react';

const NAV_SECTIONS = [
    {
        label: null,
        items: [
            { path: "/superadmin", label: "Dashboard", icon: LayoutDashboard, exact: true },
        ]
    },
    {
        label: "Clientes",
        items: [
            { path: "/superadmin/tenants", label: "Restaurantes", icon: Store },
            { path: "/superadmin/subscriptions", label: "Suscripciones", icon: Receipt },
        ]
    },
    {
        label: "Monetización",
        items: [
            { path: "/superadmin/planes", label: "Planes", icon: CreditCard },
            { path: "/superadmin/billing", label: "Facturación", icon: ShoppingCart },
        ]
    },
    {
        label: "Producto",
        items: [
            { path: "/superadmin/feature-flags", label: "Feature Flags", icon: ToggleRight },
        ]
    },
    {
        label: "Operaciones",
        items: [
            { path: "/superadmin/orders", label: "Pedidos Globales", icon: ShoppingCart },
            { path: "/superadmin/map", label: "Mapa Global", icon: Map },
            { path: "/superadmin/delivery", label: "Delivery Hub", icon: Zap },
            { path: "/superadmin/drivers", label: "Repartidores", icon: Users },
        ]
    },
    {
        label: "Sistema",
        items: [
            { path: "/superadmin/auditoria", label: "Auditoría", icon: BookOpen },
            { path: "/superadmin/events", label: "Seguridad", icon: ShieldAlert },
            { path: "/superadmin/logs", label: "Logs / Jobs", icon: Terminal },
        ]
    },
];

export default function SuperAdminLayout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Cerrar menú móvil al cambiar de ruta
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        clearSAToken();
        navigate("/superadmin/login", { replace: true });
    };

    const isActive = (item) => item.exact
        ? location.pathname === item.path
        : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

    const SidebarContent = () => (
        <>
            {/* Brand header */}
            <div className={`p-5 mb-2 border-b border-white/5 flex items-center justify-between gap-3 min-h-[68px]`}>
                <ConexoLogo variant={collapsed ? 'icon' : 'full'} size={24} />
                {!collapsed && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-primary/80 bg-primary/10 border border-primary/20 rounded-full px-2.5 py-0.5">
                        SuperAdmin
                    </span>
                )}
            </div>

            {/* Nav sections */}
            <nav className="flex-1 overflow-y-auto p-2 flex flex-col gap-0.5 custom-scrollbar">
                {NAV_SECTIONS.map((section, si) => (
                    <div key={si} className={section.label ? 'mt-4 mb-2' : ''}>
                        {section.label && !collapsed && (
                            <div className="text-[10px] font-black uppercase tracking-[0.15em] text-white/20 px-4 py-2">
                                {section.label}
                            </div>
                        )}
                        {section.items.map((item) => {
                            const Icon = item.icon;
                            const active = isActive(item);
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    end={!!item.exact}
                                    className={`
                                        flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all relative group
                                        ${collapsed ? 'justify-center' : 'justify-start'}
                                        ${active ? 'bg-primary/10 text-white border border-primary/20' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}
                                    `}
                                >
                                    <Icon size={18} className={active ? 'text-primary' : 'group-hover:text-white'} />
                                    {!collapsed && (
                                        <span className="text-sm font-bold tracking-tight">
                                            {item.label}
                                        </span>
                                    )}
                                    {active && (
                                        <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]" />
                                    )}
                                </NavLink>
                            );
                        })}
                    </div>
                ))}
            </nav>

            {/* Footer actions */}
            <div className="p-2 mt-auto border-t border-white/5 space-y-1">
                <NavLink
                    to="/superadmin/settings"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:bg-white/5 hover:text-white transition-all group"
                >
                    <Settings size={18} />
                    {!collapsed && <span className="text-sm font-bold">Configuración</span>}
                </NavLink>

                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-all group"
                >
                    <LogOut size={18} />
                    {!collapsed && <span className="text-sm font-bold">Cerrar Sesión</span>}
                </button>

                {!mobileMenuOpen && (
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="w-full p-2 flex justify-center text-white/10 hover:text-white/40 transition-colors hidden lg:flex"
                    >
                        <ChevronRight className={`transition-transform duration-300 ${collapsed ? '' : 'rotate-180'}`} size={16} />
                    </button>
                )}
            </div>
        </>
    );

    return (
        <div className="flex h-screen overflow-hidden bg-[#020617]">
            {/* Sidebar Desktop */}
            <aside
                className={`
                    hidden lg:flex flex-col bg-[#050b1d]/90 backdrop-blur-3xl border-r border-white/5 transition-all duration-300 ease-in-out z-40
                    ${collapsed ? 'w-[80px]' : 'w-[280px]'}
                `}
            >
                <SidebarContent />
            </aside>

            {/* Mobile Sidebar (Drawer) */}
            <div className={`
                fixed inset-0 z-[100] transition-opacity duration-300 lg:hidden
                ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}>
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />

                {/* Drawer */}
                <div className={`
                    absolute top-0 left-0 bottom-0 w-[280px] bg-[#050b1d] border-r border-white/5 flex flex-col transition-transform duration-300
                    ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
                `}>
                    <SidebarContent />
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="absolute top-4 right-[-48px] p-2 bg-white/5 rounded-full text-white"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
                {/* Mobile Header */}
                <header className="lg:hidden h-16 flex items-center justify-between px-6 border-b border-white/5 bg-[#020617]/80 backdrop-blur-md z-30">
                    <ConexoLogo variant="icon" size={24} />
                    <button
                        onClick={() => setMobileMenuOpen(true)}
                        className="p-2 text-slate-400 hover:text-white transition-colors"
                    >
                        <Menu size={24} />
                    </button>
                </header>

                {/* Content with Scroll */}
                <div className="flex-1 overflow-y-auto px-4 py-8 md:px-10 md:py-12 relative custom-scrollbar">
                    {/* Background halos */}
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none -z-10" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px] pointer-events-none -z-10" />

                    <div className="max-w-7xl mx-auto">
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
}
